import { useLayoutEffect, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop
 * - Scrolls window to top on route changes (pathname changes).
 * - If the URL contains a hash (#id) it will attempt to scroll to that element.
 * - Keep this component mounted inside Router (near top level) so it runs on navigation.
 */
const ScrollToTop = ({ children }) => {
  const { pathname, hash } = useLocation();

  // Prefer layout effect so the browser paints the new route already at the correct
  // scroll position (runs synchronously after DOM mutations but before paint).
  useLayoutEffect(() => {
    // Try to disable browser's automatic scroll restoration (back/forward)
    if ('scrollRestoration' in window.history) {
      try {
        window.history.scrollRestoration = 'manual';
      } catch (e) {
        // ignore
      }
    }

    // If there's a hash (e.g. /page#section) try to jump to that element immediately.
    if (hash) {
      const el = document.querySelector(hash);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
        return;
      }
    }

    // Default: jump to top instantly to avoid any visible smooth scrolling or flicker
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);

  // Restore scrollRestoration when this component unmounts (optional cleanup)
  useEffect(() => {
    return () => {
      if ('scrollRestoration' in window.history) {
        try {
          window.history.scrollRestoration = 'auto';
        } catch (e) {
          // ignore
        }
      }
    };
  }, []);

  return children || null;
};

export default ScrollToTop;
